import * as React from 'react';
import {
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    InputLabel,
    FormControl,
    Box,
    Button,
} from '@mui/material';
import TranslationHelper from '../../helpers/TranslationHelper';

interface IOwnProps {
    label?: string;
    options: { id: string; label: string }[];
    selected: { id: string; label: string }[];
    onChange: (selected: { id: string; label: string }[]) => void;
    minWidth?: number;
}

export default function MultiSelectCheckboxes({
    label = 'Select Options',
    options,
    selected,
    onChange,
    minWidth = 250,
}: IOwnProps) {
    const [open, setOpen] = React.useState(false);
    const [draftSelected, setDraftSelected] =
        React.useState<{ id: string; label: string }[]>(selected);

    const inputLabel = TranslationHelper.translate(label);

    React.useEffect(() => {
        if (!open) {
            setDraftSelected(selected);
        }
    }, [selected, open]);

    const handleItemToggle = (option: { id: string; label: string }) => {
        if (draftSelected.some((item) => item.id === option.id)) {
            setDraftSelected(
                draftSelected.filter((item) => item.id !== option.id)
            );
        } else {
            setDraftSelected([...draftSelected, option]);
        }
    };

    const handleApply = () => {
        onChange(draftSelected);
        setOpen(false);
    };

    const handleClose = () => {
        setDraftSelected(selected);
        setOpen(false);
    };

    return (
        <FormControl size="small" sx={{ minWidth }}>
            <InputLabel
                sx={{
                    color: (theme) =>
                        theme.palette.getContrastText(
                            theme.palette.primary.main
                        ),
                    '&.Mui-focused': {
                        color: (theme) =>
                            theme.palette.getContrastText(
                                theme.palette.primary.main
                            ),
                    },
                }}
            >
                {inputLabel}
            </InputLabel>
            <Select
                variant="outlined"
                label={inputLabel}
                multiple
                open={open}
                onOpen={() => setOpen(true)}
                onClose={handleClose}
                value={draftSelected}
                sx={{
                    '& .MuiSelect-select': {
                        color: (theme) =>
                            theme.palette.getContrastText(
                                theme.palette.primary.main
                            ),
                    },
                }}
                renderValue={(selected) =>
                    selected.length === 0
                        ? inputLabel
                        : selected.map((s) => s.label).join(', ')
                }
            >
                {options.map((option) => (
                    <MenuItem
                        key={option.id}
                        value={option.id}
                        onClick={() => {
                            return handleItemToggle(option);
                        }}
                    >
                        <Checkbox
                            checked={draftSelected.some(
                                (item) => item.id === option.id
                            )}
                        />
                        <ListItemText primary={option.label} />
                    </MenuItem>
                ))}
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    p={1}
                    mt={1}
                    borderTop="1px solid #eee"
                >
                    <Button
                        size="small"
                        variant="contained"
                        onClick={handleApply}
                    >
                        {TranslationHelper.translate('Filter')}
                    </Button>
                </Box>
            </Select>
        </FormControl>
    );
}
